<script setup lang="ts">
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useInboxStore } from '@/features/inbox/stores/inboxStore';
import type { InboxNotification } from '@/features/inbox/api/notificationApi';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import { useInboxNavigation } from '@/features/inbox/composables/useInboxNavigation';
import { useInboxTimeFormat } from '@/features/inbox/composables/useInboxTimeFormat';
import { useRouter } from 'vue-router';

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = computed(() => createSyncT(languageStore.currentLanguage));

function tr(key: string, params?: Record<string, string | number>): string {
  let text = t.value(key) || key;
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    });
  }
  return text;
}

const inboxStore = useInboxStore();
const { openNotification } = useInboxNavigation();
const { formatTime } = useInboxTimeFormat();
const router = useRouter();

const newBadgeLabel = computed(() => tr('common.inbox.newBadge'));
const hasUnread = computed(() => inboxStore.unreadCount > 0);
const showBellNew = computed(() => hasUnread.value || inboxStore.hasNewArrival);

const subtitleText = computed(() => {
  const n = inboxStore.unreadCount;
  if (n === 0) return tr('common.inbox.noUnread');
  return tr('common.inbox.unreadCount', { count: n });
});

function eventIcon(eventType: string): string {
  switch (eventType) {
    case 'N01001':
      return 'check-circle-bold-duotone';
    case 'N01002':
      return 'close-circle-bold-duotone';
    case 'N01003':
      return 'document-text-bold-duotone';
    default:
      return 'bell-bing-bold-duotone';
  }
}

function eventColor(eventType: string): string {
  switch (eventType) {
    case 'N01001':
      return 'success';
    case 'N01002':
      return 'error';
    case 'N01003':
      return 'primary';
    default:
      return 'secondary';
  }
}

async function onMenuOpen(isOpen: boolean) {
  inboxStore.menuOpen = isOpen;
  if (isOpen) {
    await inboxStore.loadList();
    inboxStore.items.forEach((item) => {
      if (inboxStore.isUnread(item)) {
        inboxStore.markNewArrival(item.notificationId);
      }
    });
  }
}

async function onItemClick(item: InboxNotification) {
  await openNotification(item, { closeMenu: true });
}

async function onMarkAllRead() {
  await inboxStore.markAllRead();
}

async function goToInbox() {
  inboxStore.menuOpen = false;
  await router.push('/pages/inbox');
}
</script>

<template>
  <v-menu
    v-model="inboxStore.menuOpen"
    :open-on-hover="false"
    location="bottom end"
    class="notification_popup"
    :z-index="2600"
    content-class="inbox-notification-menu-content"
    @update:model-value="onMenuOpen"
  >
    <template #activator="{ props: menuProps }">
      <v-btn
        icon
        class="custom-hover-primary mx-1 inbox-bell-btn"
        size="small"
        variant="text"
        color="primary"
        v-bind="menuProps"
        :aria-label="tr('common.inbox.title')"
      >
        <v-badge
          :model-value="showBellNew"
          color="error"
          :content="newBadgeLabel"
          floating
          offset-x="2"
          offset-y="4"
          class="inbox-bell-badge"
        >
          <Icon icon="solar:bell-bing-line-duotone" height="20" />
        </v-badge>
      </v-btn>
    </template>

    <v-sheet rounded="md" width="360" elevation="12" class="inbox-menu-panel bg-surface">
      <div class="px-6 pt-4 pb-4 bg-primary d-flex align-center justify-space-between">
        <div>
          <div class="d-flex align-center gap-2">
            <h6 class="text-h5 text-white mb-0">{{ tr('common.inbox.title') }}</h6>
            <v-chip
              v-if="hasUnread"
              size="x-small"
              color="error"
              variant="flat"
              class="inbox-header-new"
              label
            >
              {{ newBadgeLabel }}
            </v-chip>
          </div>
          <p class="text-12 text-white text-opacity-80 mb-0 mt-1">{{ subtitleText }}</p>
        </div>
        <v-btn
          v-if="hasUnread"
          size="small"
          variant="text"
          class="text-white"
          @click.stop="onMarkAllRead"
        >
          {{ tr('common.inbox.markAllRead') }}
        </v-btn>
      </div>

      <perfect-scrollbar style="height: 350px" class="inbox-menu-scroll bg-surface">
        <v-list v-if="inboxStore.loading" class="py-8 text-center bg-surface">
          <v-progress-circular indeterminate color="primary" size="32" />
        </v-list>
        <v-list v-else-if="inboxStore.items.length === 0" class="py-8 text-center textSecondary bg-surface">
          {{ tr('common.inbox.empty') }}
        </v-list>
        <v-list v-else class="py-0 theme-list bg-surface" lines="two">
          <v-list-item
            v-for="item in inboxStore.items"
            :key="item.notificationId"
            :value="item"
            color="primary"
            class="py-3 px-4 border-b inbox-list-item"
            :class="{ 'inbox-list-item--unread': inboxStore.isUnread(item) }"
            @click="onItemClick(item)"
          >
            <template #prepend>
              <v-avatar size="40" :color="'light' + eventColor(item.eventType)" rounded="circle">
                <Icon
                  :icon="'solar:' + eventIcon(item.eventType)"
                  height="20"
                  :class="'text-' + eventColor(item.eventType)"
                />
              </v-avatar>
            </template>

            <v-list-item-title class="d-flex align-center gap-2">
              <span class="text-truncate">{{ item.title }}</span>
            </v-list-item-title>

            <v-list-item-subtitle v-if="item.body" class="text-truncate">
              {{ item.body }}
            </v-list-item-subtitle>

            <template #append>
              <div class="d-flex flex-column align-end gap-1 flex-shrink-0 ms-2">
                <v-chip
                  v-if="inboxStore.showNewBadge(item)"
                  size="x-small"
                  color="error"
                  variant="flat"
                  class="inbox-item-new"
                  label
                >
                  {{ newBadgeLabel }}
                </v-chip>
                <span class="text-12 textSecondary">{{ formatTime(item.sysregdate) }}</span>
              </div>
            </template>
          </v-list-item>
        </v-list>
      </perfect-scrollbar>
      <div class="py-3 px-6 text-center bg-surface border-t">
        <v-btn
          size="small"
          variant="flat"
          color="primary"
          block
          @click.stop="goToInbox"
        >
          {{ tr('common.inbox.goToInbox') }}
        </v-btn>
      </div>
    </v-sheet>
  </v-menu>
</template>

<style scoped lang="scss">
:global(.inbox-notification-menu-content) {
  z-index: 2600 !important;
  overflow: visible;
}

.inbox-menu-panel {
  overflow: hidden;
  background: rgb(var(--v-theme-surface)) !important;
}

.inbox-menu-scroll {
  background: rgb(var(--v-theme-surface));
}

.inbox-bell-btn {
  overflow: visible !important;
}

.inbox-bell-badge :deep(.v-badge__badge) {
  font-size: 9px !important;
  font-weight: 700;
  letter-spacing: 0.04em;
  min-width: 28px !important;
  height: 14px !important;
  padding: 0 4px !important;
}

.inbox-header-new {
  height: 18px !important;
  font-size: 10px !important;
  font-weight: 700;
}

.inbox-list-item--unread {
  background-color: rgba(var(--v-theme-primary), 0.06);
}

.inbox-item-new {
  height: 18px !important;
  font-size: 10px !important;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0 6px !important;
}
</style>
